import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidatorFn } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBase } from '../../utils/form-base';

interface Opcao {
  id: number;
  resposta: string;
  valor: number;
}

interface Questao {
  id?: string;
  perguntaId?: string;
  pergunta: string;
  opcoes: Opcao[];
  resposta?: number;
  peso: number;
}

@Component({
  selector: 'app-questionnaire',
  templateUrl: './questionnaire.component.html',
  styleUrls: ['./questionnaire.component.scss']
})
export class QuestionnaireComponent extends FormBase implements OnInit {
  @Input() parentForm: FormGroup;
  @Input() isEditable = true;
  @Input() showScore = false;
  @Output() sumValue = new EventEmitter();
  @Output() onChangeResponse = new EventEmitter();

  @Input() questions: Questao[];
  questionnaireForm: FormGroup;


  constructor(
    public router: Router,
    public activatedRoute: ActivatedRoute,
    private formBuilder: FormBuilder
  ) {
    super(router, activatedRoute);
  }

  ngOnInit() {
    this.createFormGroup();
  }

  createFormGroup() {
    this.questionnaireForm = this.formBuilder.group({
      questionsArray: this.formBuilder.array(
        []
      )
    });

    this.initForm();
    this.parentForm?.setControl('questionario', this.questionnaireForm);
  }


  initForm() {
    const questionArray = this.questionnaireForm.get('questionsArray') as FormArray;

    this.questions.forEach((question) => {
      const optionArray = this.formBuilder.array(
        question.opcoes.map((option) => {
          return this.formBuilder.group({
            perguntaId: new FormControl(question.perguntaId || question.id),
            selected: new FormControl(question.resposta === option.valor),
            value: new FormControl(option.valor)
          });
        })
      );

      optionArray.setValidators(this.atLeastOneSelected());

      questionArray.push(
        this.formBuilder.group({
          opcoes: optionArray,
          pontuacao: new FormControl(question.resposta),
          peso: new FormControl(5),
          totalPontos: new FormControl(question.resposta * 5)
        })
      );

    });

    this.sum();
  }


  atLeastOneSelected(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const questions = control.value;
      const hasAtLeastOneSelected = questions?.some((option) => option.selected);

      return hasAtLeastOneSelected ? null : { noOptionSelected: true };
    };
  }


  isChecked(option) {
    return option.get('selected').value;
  }


  markOption(question, option) {
    if (!this.isEditable) return;
    this.uncheckAll(question);
    option.get('selected').patchValue(true);
    this.sum();
    this.respostas();
  }

  uncheckAll(question) {
    question.controls.forEach((option: FormControl) => {
      option.get('selected').patchValue(false);
    });
  }

  questionTopico(index: number): string {
    const pergunta = this.questions[index].pergunta;
    const parts = pergunta.split(':');
    return parts.length > 1 ? parts[0].trim() : pergunta;
  }

  questionDescricao(index: number): string {
    const pergunta = this.questions[index].pergunta;
    const parts = pergunta.split(':');
    return parts.length > 1 ? parts.slice(1).join(':').trim() : '';
  }

  questionOptiondescricao(questionIndex: number, optionIndex: number): string {
    const resposta = this.questions[questionIndex].opcoes[optionIndex].resposta;
    if (resposta && !resposta.match(/[.!?]$/)) {
      return resposta + '.';
    }
    return resposta;
  }

  sum() {
    const questionsArray = this.questionnaireForm.get('questionsArray') as FormArray;
    const selected = [].concat(...questionsArray.value.map(x => x.opcoes.filter(y => y.selected)));
    const sum = selected?.map(x => x.value)?.reduce((a, b) => a + b, 0);

    (questionsArray.controls as FormGroup[]).forEach((questionControl) => {
      const opcoes = (questionControl.get('opcoes') as FormArray).value;
      const selectedOption = opcoes.find(o => o.selected);
      const pontuacao = selectedOption ? selectedOption.value : 0;
      const peso = questionControl.get('peso').value;
      questionControl.patchValue({ pontuacao, totalPontos: pontuacao * peso }, { emitEvent: false });
    });

    this.sumValue.emit(sum);
  }

  respostas() {
    const selected = [].concat(...this.questionnaireForm.get('questionsArray').value.map(x => x.opcoes.filter(y => y.selected)));
    const respostas = selected.map(x => {
      return {
        perguntaId: x.perguntaId,
        resposta: x.value
      };
    });
    this.onChangeResponse.emit(respostas);
  }
}
